import { Rect, TableMap, TableRect } from '@tiptap/pm/tables';
import { Editor } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
   getCellsInRow,
   getCellsInColumn,
   isTableSelected,
   isRowSelected,
   isColumnSelected,
   findTable,
   isInTableMap,
   isCursorInRow,
   isCursorInColumn,
} from './utils/table_utils';
import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';

type TableLocation = 'row' | 'column' | 'table';

interface BuildModifyContainerProps {
   location: TableLocation;
   rect: Rect;
   editor: Editor;
   selected?: boolean;
   previousSelected?: boolean;
}

const buildModifyContainer = ({
   location,
   rect,
   editor,
   selected,
   previousSelected,
}: BuildModifyContainerProps): HTMLElement => {
   let className = 'modify-' + location + '-container';

   if (selected && !previousSelected) {
      className += ' selected';
   }

   const modifyContainer = document.createElement('div');
   modifyContainer.className = className;

   // The first element needs an extra button to insert before
   if (rect.left === 0 && rect.top === 0 && location !== 'table') {
      const side: InsertSide = 'before';
      const insertBeforeContainer = buildInsertContainer({ location, side, rect, editor });
      modifyContainer.append(insertBeforeContainer);
   }

   const deleteButton = buildDeleteButton({ location, editor });
   modifyContainer.append(deleteButton);

   if (location !== 'table') {
      const insertAfterContainer = buildInsertContainer({ location, side: 'after', rect, editor });
      modifyContainer.append(insertAfterContainer);
   }

   return modifyContainer;
};

interface BuildDeleteButtonProps {
   location: TableLocation;
   editor: Editor;
}

const buildDeleteButton = ({ location, editor }: BuildDeleteButtonProps): HTMLElement => {
   const [width, height, _1, _2, path] = faTimes.icon;
   const deleteIcon = `
      <svg style="height: 1em" viewBox="0,0,${width},${height}">
         <path fill="currentColor" d="${path}" />
      </svg>
   `;

   const deleteButton = document.createElement('button');
   deleteButton.classList.add('widget-button');
   deleteButton.classList.add('delete-button');
   deleteButton.classList.add('delete-' + location);

   deleteButton.innerHTML = deleteIcon;
   deleteButton.addEventListener('mousedown', event => {
      event.preventDefault();
      if (location === 'row') {
         editor.commands.deleteRow();
      } else if (location === 'column') {
         editor.commands.cleanDeleteColumn();
      } else if (location === 'table') {
         editor.commands.deleteTable();
      }
   });

   return deleteButton;
};

type InsertLocation = Exclude<TableLocation, 'table'>;
type InsertSide = 'before' | 'after';

interface BuildInsertContainerProps {
   location: InsertLocation;
   side: InsertSide;
   rect: Rect;
   editor: Editor;
}

const buildInsertContainer = ({
   location,
   side,
   rect,
   editor,
}: BuildInsertContainerProps): HTMLElement => {
   const insertButton = buildInsertButton({ location, side, rect, editor });

   const insertContainer = document.createElement('div');
   insertContainer.className = 'insert-container';
   insertContainer.append(insertButton);
   return insertContainer;
};

interface BuildInsertButtonProps {
   location: InsertLocation;
   side: InsertSide;
   rect: Rect;
   editor: Editor;
}

const buildInsertButton = ({
   location,
   side,
   rect,
   editor,
}: BuildInsertButtonProps): HTMLElement => {
   const [width, height, _1, _2, path] = faPlus.icon;
   const plusIcon = `
      <svg style="height: 1em" viewBox="0,0,${width},${height}">
         <path fill="currentColor" d="${path}" />
      </svg>
   `;

   const insertButton = document.createElement('button');
   insertButton.classList.add('widget-button');
   insertButton.classList.add('insert-button');
   insertButton.classList.add('insert-' + location);
   insertButton.classList.add('insert-' + side);

   insertButton.innerHTML = plusIcon;

   insertButton.addEventListener('mousedown', event => {
      event.preventDefault();

      const rowIndex = side === 'before' ? rect.top : rect.bottom;
      const columnIndex = side === 'before' ? rect.left : rect.right;

      if (location === 'row') {
         editor.commands.addRow(rowIndex);
      } else if (location === 'column') {
         editor.commands.addColumn(columnIndex);
      }
   });

   return insertButton;
};

interface BuildSelectorProps {
   location: TableLocation;
   rect: Rect;
   editor: Editor;
   active?: boolean;
   selected?: boolean;
}

const buildSelector = ({
   location,
   rect,
   editor,
   active,
   selected,
}: BuildSelectorProps): HTMLElement => {
   let className = 'select-' + location;

   if (active) {
      className += ' active';
   }

   if (selected) {
      className += ' selected';
   }

   const selectButton = document.createElement('button');
   selectButton.addEventListener('mousedown', event => {
      event.preventDefault();
      if (location === 'row') {
         editor.commands.selectRow(rect.top);
      } else if (location === 'column') {
         editor.commands.selectColumn(rect.left);
      } else if (location === 'table') {
         editor.commands.selectTable();
      }
   });

   const selector = document.createElement('div');
   selector.className = className;
   selector.append(selectButton);

   return selector;
};

const getTableDecorations = (editor: Editor, tableRect: TableRect): Decoration[] => {
   const location: TableLocation = 'table';

   const { tableStart, map, table } = tableRect;
   const relativePos = map.positionAt(0, 0, table);
   const rect = map.findCell(relativePos);
   const pos = relativePos + tableStart;

   const selection = editor.state.selection;
   const selected = isTableSelected(selection);
   const active = false;

   return [
      Decoration.widget(pos + 1, () => {
         const selector = buildSelector({ location, rect, editor, active, selected });

         return selector;
      }),
      Decoration.widget(pos + 1, () => {
         const modifyContainer = buildModifyContainer({ location, rect, editor, selected });

         return modifyContainer;
      }),
   ];
};

const getRowDecorations = (editor: Editor, tableRect: TableRect): Decoration[] => {
   const location: TableLocation = 'row';

   const { tableStart, map } = tableRect;

   const selection = editor.state.selection;
   const cells = getCellsInColumn(selection, 0);
   if (!cells) {
      return [];
   }

   const decorations: Decoration[] = [];
   cells.forEach(({ pos }) => {
      const rect = map.findCell(pos - tableStart);

      decorations.push(
         Decoration.widget(pos + 1, () => {
            const selected = isRowSelected(selection, rect.top);
            const active = isCursorInRow(selection, rect.top);
            const selector = buildSelector({ location, rect, editor, active, selected });

            return selector;
         }),
         Decoration.widget(pos + 1, () => {
            const rowSelected = isRowSelected(selection, rect.top, { strict: true });
            const selected = rowSelected && !isTableSelected(selection);

            const inTableMap = isInTableMap(rect.top - 1, 0, map);
            const previousSelected = inTableMap && isRowSelected(selection, rect.top - 1);

            const modifyContainer = buildModifyContainer({
               location,
               rect,
               editor,
               selected,
               previousSelected,
            });

            return modifyContainer;
         })
      );
   });

   return decorations;
};

const getColumnDecoration = (editor: Editor, tableRect: TableRect): Decoration[] => {
   const location: TableLocation = 'column';

   const { tableStart, map } = tableRect;

   const selection = editor.state.selection;
   const cells = getCellsInRow(selection, 0);
   if (!cells) {
      return [];
   }

   const decorations: Decoration[] = [];
   cells.forEach(({ pos }) => {
      const rect = map.findCell(pos - tableStart);

      decorations.push(
         Decoration.widget(pos + 1, () => {
            const selected = isColumnSelected(selection, rect.left);
            const active = isCursorInColumn(selection, rect.left);
            const selector = buildSelector({ location, rect, editor, active, selected });

            return selector;
         }),
         Decoration.widget(pos + 1, () => {
            const columnSelected = isColumnSelected(selection, rect.left, { strict: true });
            const selected = columnSelected && !isTableSelected(selection);

            const inTableMap = isInTableMap(0, rect.left - 1, map);
            const previousSelected = inTableMap && isColumnSelected(selection, rect.left - 1);

            const modifyContainer = buildModifyContainer({
               location,
               rect,
               editor,
               selected,
               previousSelected,
            });

            return modifyContainer;
         })
      );
   });

   return decorations;
};

export const inlinetableDecorations = (editor: Editor) => {
   return new Plugin({
      props: {
         decorations: ({ doc }) => {
            if (!editor.view.hasFocus()) {
               return;
            }

            const table = findTable(editor.state.selection);
            if (!table) {
               return;
            }

            const tableMap = TableMap.get(table.node);
            const tableRect: TableRect = {
               tableStart: table.start,
               map: tableMap,
               table: table.node,

               // We don't use these fields
               left: 0,
               right: 0,
               top: 0,
               bottom: 0,
            };

            const decorations: Decoration[] = [
               ...getTableDecorations(editor, tableRect),
               ...getRowDecorations(editor, tableRect),
               ...getColumnDecoration(editor, tableRect),
            ];

            return DecorationSet.create(doc, decorations);
         },
      },
   });
};
